import React from 'react'
import './WhatsAppEditor.css'

interface WhatsAppPreviewProps {
  message: string
  header?: {
    type: 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'TEXT'
    text?: string
    url?: string
  }
  footer?: string
  buttons?: Array<{
    type: 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER'
    text: string
  }>
}

/**
 * WhatsApp Message Preview
 * 
 * Shows how the message will look in WhatsApp with proper formatting
 */
export const WhatsAppPreview: React.FC<WhatsAppPreviewProps> = ({
  message,
  header,
  footer,
  buttons
}) => {
  const formatWhatsAppMessage = (text: string): React.ReactNode => {
    if (!text) return null

    // Helper to process line formatting
    const processLine = (line: string, lineIndex: number) => {
      // 1. Process variables {{...}} or ${...}
      const parts = line.split(/(\{\{.*?\}\}|\$\{.*?\})/g)
      
      return parts.map((part, partIndex) => {
        if (part.match(/^(\{\{.*?\}\}|\$\{.*?\})$/)) {
          return (
            <span key={`${lineIndex}-${partIndex}`} className="whatsapp-variable">
              {part}
            </span>
          )
        }
        
        return processFormatting(part, 0, `${lineIndex}-${partIndex}`)
      })
    }

    // Regex patterns for WhatsApp formatting
    const patterns = [
        {regex: /\*([^*]+)\*/g, tag: 'strong'},      // Bold
        {regex: /_([^_]+)_/g, tag: 'em'},            // Italic
        {regex: /~([^~]+)~/g, tag: 's'},             // Strikethrough
        {regex: /```([^`]+)```/g, tag: 'code'},      // Monospace
    ]

    const processFormatting = (text: string, patternIndex: number, keyPrefix: string): React.ReactNode[] => {
        if (patternIndex >= patterns.length) return [text]

        const pattern = patterns[patternIndex]
        const matches = Array.from(text.matchAll(pattern.regex))
        
        if (matches.length === 0) return processFormatting(text, patternIndex + 1, keyPrefix)

        const result: React.ReactNode[] = []
        let lastIndex = 0

        matches.forEach((match, i) => {
            if (match.index! > lastIndex) {
                result.push(...processFormatting(text.substring(lastIndex, match.index), patternIndex + 1, `${keyPrefix}-pre-${i}`))
            }
            
            const Tag = pattern.tag as keyof JSX.IntrinsicElements
            result.push(
                <Tag key={`${keyPrefix}-match-${i}`}>
                    {processFormatting(match[1], patternIndex + 1, `${keyPrefix}-inner-${i}`)}
                </Tag>
            )
            
            lastIndex = match.index! + match[0].length
        })

        if (lastIndex < text.length) {
            result.push(...processFormatting(text.substring(lastIndex), patternIndex + 1, `${keyPrefix}-post`))
        }

        return result
    }

    return text.split('\n').map((line, i) => (
      <div key={i} style={{minHeight: line.trim() ? 'auto' : '1.2em'}}>
        {processLine(line, i)}
      </div>
    ))
  }

  // Determine header content
  const renderHeader = () => {
    if (!header) return null

    if (header.type === 'IMAGE') {
      return (
        <div className="whatsapp-message-header">
           {header.url ? (
             <img src={header.url} alt="Header" className="whatsapp-header-image" />
           ) : (
             <div className="whatsapp-header-placeholder">
               <i className="bi bi-image fs-1 d-block mb-2"></i>
               <span>Imagen</span>
             </div>
           )}
        </div>
      )
    }
    if (header.type === 'VIDEO') {
        return (
          <div className="whatsapp-message-header">
             <div className="whatsapp-header-placeholder">
               <i className="bi bi-camera-video fs-1 d-block mb-2"></i>
               <span>Video</span>
             </div>
          </div>
        )
      }
      if (header.type === 'DOCUMENT') {
        return (
          <div className="whatsapp-message-header">
             <div className="whatsapp-header-placeholder">
               <i className="bi bi-file-earmark-text fs-1 d-block mb-2"></i>
               <span>Documento</span>
             </div>
          </div>
        )
      }
      return null
  }

  return (
    <div className='whatsapp-preview'>
      <div className='whatsapp-preview-header'>
        <div className='d-flex align-items-center gap-2'>
          <i className='ki-outline ki-whatsapp fs-2 text-success'></i>
          <div>
            <div className='fw-bold'>Vista Previa WhatsApp</div>
            <div className='text-muted small'>Así se verá tu mensaje</div>
          </div>
        </div>
      </div>
      
      <div className='whatsapp-message-container'>
        <div className='whatsapp-message-bubble'>
            {renderHeader()}
            
            <div className="whatsapp-message-body">
                {formatWhatsAppMessage(message)}
            </div>
            
            {footer && <div className="whatsapp-message-footer">{footer}</div>}
        </div>

        {buttons && buttons.length > 0 && (
            <div className="whatsapp-buttons-container">
                {buttons.map((btn, i) => (
                    <div key={i} className="whatsapp-button">
                        {btn.type === 'URL' && <i className="bi bi-box-arrow-up-right me-2 small"></i>}
                        {btn.type === 'PHONE_NUMBER' && <i className="bi bi-telephone me-2 small"></i>}
                        {btn.type === 'QUICK_REPLY' && <i className="bi bi-reply me-2 small"></i>}
                        {btn.text}
                    </div>
                ))}
            </div>
        )}
      </div>

      <div className='whatsapp-preview-footer'>
        <small className='text-muted'>
          <i className='bi bi-info-circle me-1'></i>
          Los emojis y variables se renderizan en el mensaje real
        </small>
      </div>
    </div>
  )
}
